import React, { ComponentProps, useEffect, useRef, JSX } from 'react';

import * as analytics from '@adsk/acs-web-analytics';
import { MenuItem } from '@adsk/alloy-react-menu';
import theme from '@adsk/alloy-react-theme';

import {
  MentionData,
  MentionType,
  MENTION_ANALYTICS,
  MENTION_TYPES,
} from '../consts';
import { MentionCompanySuggestion } from './MentionSuggestionComponents/MentionCompanySuggestion';
import { MentionRoleSuggestion } from './MentionSuggestionComponents/MentionRoleSuggestion';
import { MentionUserSuggestion } from './MentionSuggestionComponents/MentionUserSuggestion';

export type MentionSuggestionProps = ComponentProps<typeof MenuItem> & {
  className?: string;
  mention: MentionData;
  isFocused?: boolean;
  searchValue?: string;
  renderSuggestion?: ({
    suggestion,
    searchValue,
  }: {
    suggestion: MentionData;
    searchValue?: string;
  }) => JSX.Element | null;
};

const MENTION_SUGGESTIONS = {
  [MENTION_TYPES.USER]: MentionUserSuggestion,
  [MENTION_TYPES.COMPANY]: MentionCompanySuggestion,
  [MENTION_TYPES.ROLE]: MentionRoleSuggestion,
};

const renderDefaultSuggestions = ({
  suggestion,
  searchValue: search,
}: {
  suggestion: MentionData;
  searchValue?: string;
}) => {
  const Suggestion =
    MENTION_SUGGESTIONS[suggestion.type?.toLocaleLowerCase() as MentionType];

  if (!Suggestion) {
    const error = `renderDefaultSuggestions: No mention suggestion for type ${suggestion.type}`;

    console.error(error);
    analytics.track(MENTION_ANALYTICS.ERROR, { error });

    return null;
  }

  return <Suggestion suggestion={suggestion} searchValue={search} />;
};

const MentionSuggestion = ({
  className,
  style,
  mention,

  isFocused,
  searchValue,
  onMouseEnter, // Not in use to enable hover state
  onMouseDown,
  onMouseUp,

  renderSuggestion = renderDefaultSuggestions,
  ...props
}: MentionSuggestionProps) => {
  const ref = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    if (isFocused) {
      ref.current?.scrollIntoView(false);
    }
  }, [isFocused]);

  return (
    <MenuItem
      {...props}
      ref={ref}
      className={className}
      showSelection={false}
      onMouseDown={onMouseDown}
      onMouseUp={onMouseUp}
      style={[
        {
          padding: '12px 16px',
          textAlign: 'left',
          height: 50,

          ...(isFocused && {
            background: theme.colors.adskBlue100,
            '&:active': {
              background: theme.colors.adskBlue100,
            },
            '&:hover': {
              background: theme.colors.adskBlue100,
            },
          }),
        },
        style,
      ]}
    >
      {renderSuggestion({
        suggestion: mention,
        searchValue,
      })}
    </MenuItem>
  );
};

MentionSuggestion.displayName = 'MentionSuggestion';

export default MentionSuggestion;
